import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { GooglePlaceModule } from 'ngx-google-places-autocomplete-esb';
import { Address } from 'ngx-google-places-autocomplete-esb/lib/objects/address';

import { LocalisePipe } from '../../../../modules/pipes/localise.pipe';
import { ThemesService } from '../../../../services/themes.service';
import { ProductEnum } from '../../../authentication/models/product.enum';
import { User } from '../../../authentication/models/v1-api.interface';
import { AuthenticationService } from '../../../authentication/services/authentication.service';
import { FormControlNameExport } from '../../../directives/form-control-name-export.directive';
import { DisableAutofillDirective } from '../../../shared/directives/disable-autofill.directive';
import { SharedService } from '../../../shared/services/shared.service';
import { StatesService } from '../../../shared/services/states.service';
import { ParsedAddressInterface } from '../../../signup/models/parsed-address.interface';
import { InviteSignupProcessService } from '../../services/invite-signup-process.service';

@Component({
  selector: 'app-invite-signup-process-identity',
  templateUrl: './invite-signup-process-identity.component.html',
  styleUrls: ['./invite-signup-process-identity.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatError,
    MatIconModule,
    MatButtonModule,
    GooglePlaceModule,
    DisableAutofillDirective,
    LocalisePipe,
    FormControlNameExport,
    MatCheckbox,
  ],
})
export class InviteSignupProcessIdentityComponent implements OnInit {
  public loading = false;
  public error = '';
  private readonly lestOneEmpty: Array<keyof User> = [
    'firstName',
    'lastName',
    'line1Address',
    'city',
    'state',
    'zip',
    'shippingLine1Address',
  ];

  constructor(
    public inviteSignupProcessService: InviteSignupProcessService,
    public statesService: StatesService,
    public themesService: ThemesService,
    private sharedService: SharedService,
    private authenticationService: AuthenticationService,
  ) {}

  ngOnInit(): void {
    const user = this.authenticationService.getProfileState();
    if (user.productIds.includes(ProductEnum.Payment)) {
      this.inviteSignupProcessService.nextStep();
      return;
    }
    if (
      !user.productIds.includes(ProductEnum.Advance) ||
      !this.lestOneEmpty.some((field) => !user[field] && user[field] !== false)
    ) {
      this.inviteSignupProcessService.nextStep();
      return;
    }
  }

  public async submit(): Promise<void> {
    this.loading = true;

    try {
      await this.inviteSignupProcessService.updateIdentity().toPromise();
      this.inviteSignupProcessService.nextStep();
    } catch (e) {
      this.error = this.sharedService.getErrorText(
        e,
        true,
        this.inviteSignupProcessService.identityForm,
      );
    }

    this.loading = false;
  }

  public setAutoFillOff(event: any): void {
    if (event) {
      event.target.attributes.autocomplete.value = Math.random()
        .toString(36)
        .slice(-6);
    }
  }

  /**
   * Set the address when selected from google maps.
   */
  public handleAddressChange(address: Address): void {
    if (!address.address_components) {
      return;
    }

    const parsedAddress: ParsedAddressInterface = {};

    address.address_components.forEach((v) => {
      v.types.forEach((t) => {
        const trimmedType = t.trim();
        if (trimmedType === 'administrative_area_level_1') {
          parsedAddress.state_abbr = v.short_name?.trim() || '';
        }
        if (trimmedType === 'country') {
          parsedAddress.country_code = v.short_name?.trim() || '';
        }
        parsedAddress[t as keyof ParsedAddressInterface] =
          v.long_name?.trim() || '';
      });
    });

    if (
      this.statesService.usStatesList.includes(
        parsedAddress?.state_abbr as string,
      )
    ) {
      this.inviteSignupProcessService.identityForm.controls.state?.setValue(
        parsedAddress?.state_abbr?.trim() || '',
      );
    }
    this.inviteSignupProcessService.identityForm.controls.line1Address?.setValue(
      `${parsedAddress?.street_number?.trim() || ''} ${
        parsedAddress?.route?.trim() || ''
      }`.trim(),
    );
    this.inviteSignupProcessService.identityForm.controls.city?.setValue(
      parsedAddress?.locality?.trim() || '',
    );
    this.inviteSignupProcessService.identityForm.controls.zip?.setValue(
      parsedAddress?.postal_code?.trim() || '',
    );
  }

  /**
   * Set the address when selected from google maps.
   */
  public handleShippingAddressChange(address: Address): void {
    if (!address.address_components) {
      return;
    }

    const parsedAddress: ParsedAddressInterface = {};

    address.address_components.forEach((v) => {
      v.types.forEach((t) => {
        const trimmedType = t.trim();
        if (trimmedType === 'administrative_area_level_1') {
          parsedAddress.state_abbr = v.short_name?.trim() || '';
        }
        if (trimmedType === 'country') {
          parsedAddress.country_code = v.short_name?.trim() || '';
        }
        parsedAddress[t as keyof ParsedAddressInterface] =
          v.long_name?.trim() || '';
      });
    });

    if (
      this.statesService.usStatesList.includes(
        parsedAddress?.state_abbr as string,
      )
    ) {
      this.inviteSignupProcessService.identityForm.controls.shippingState?.setValue(
        parsedAddress?.state_abbr?.trim() || '',
      );
    }
    this.inviteSignupProcessService.identityForm.controls.line1Address?.setValue(
      `${parsedAddress?.street_number?.trim() || ''} ${
        parsedAddress?.route?.trim() || ''
      }`.trim(),
    );
    this.inviteSignupProcessService.identityForm.controls.shippingCity?.setValue(
      parsedAddress?.locality?.trim() || '',
    );
    this.inviteSignupProcessService.identityForm.controls.shippingZip?.setValue(
      parsedAddress?.postal_code?.trim() || '',
    );
  }

  public onSendToHomeAddressChanged(): void {
    [
      'shippingZip',
      'shippingCity',
      'shippingState',
      'shippingLine1Address',
    ].forEach((field) =>
      this.inviteSignupProcessService.identityForm.controls[
        field
      ].updateValueAndValidity(),
    );
  }
}
